import { useEffect, useRef } from 'react';
import { API } from '../../config';
import { replaceSignatureBlock, signatureInputError } from './emailSignature';

export const useEmailSignature = (isOpen, modalData, onUpdate) => {
  const version = useRef(0);
  const controller = useRef(null);
  const context = `${modalData.petId}:${modalData.templateKey}`;

  useEffect(() => {
    return () => {
      version.current += 1;
      controller.current?.abort();
    };
  }, [isOpen, context]);

  return async (signature) => {
    const requestVersion = ++version.current;
    controller.current?.abort();
    const validationError = signatureInputError(signature);
    onUpdate(prev => ({ ...prev, signature, signatureLoading: !validationError, signatureError: validationError }));
    if (validationError) return;
    controller.current = new AbortController();
    try {
      const response = await fetch(`${API}/email/signature/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        signal: controller.current.signal, body: JSON.stringify(signature),
      });
      const data = await response.json();
      if (!response.ok) {
        const detail = Array.isArray(data.detail) ? data.detail.map(item => item.msg).join('; ') : data.detail;
        throw new Error(detail || 'Unable to update the signature. Please try again.');
      }
      if (version.current !== requestVersion) return;
      onUpdate(prev => {
        try {
          return {
            ...prev,
            // Keep in-progress spaces in controlled inputs; the server trims
            // only the rendered signature, not the staff member's typing.
            signature: signature.choice === 'custom' ? signature : data.signature,
            signatureHtml: data.html,
            renderedBody: replaceSignatureBlock(prev.renderedBody, data.html),
            signatureLoading: false, signatureError: '',
          };
        } catch (error) {
          return { ...prev, signatureLoading: false, signatureError: error.message };
        }
      });
    } catch (error) {
      if (version.current !== requestVersion || error.name === 'AbortError') return;
      onUpdate(prev => ({ ...prev, signatureLoading: false, signatureError: error.message }));
    }
  };
};